import { Painter, flipH } from './pixel';
import { tagRaster } from '@/engine/canvas';
import { C } from './palette';
import type { Facing } from '@/engine/math';

export const MON_W = 32;
export const MON_H = 48;
export const MON_FRAMES = 24;
export const monsterBob = (n: number) => Math.round(1 - Math.cos(n / MON_FRAMES * Math.PI * 2)) / 2;
export interface MonsterSprites {
  frames: Record<Facing, HTMLCanvasElement[]>;
  eyes: Record<Facing, Array<[number, number]>>;
}
function line(p:Painter,x:number,y:number,ex:number,ey:number,w:number,c:string){
  const n=Math.max(Math.abs(ex-x),Math.abs(ey-y));
  for(let i=0;i<=n;i++)p.rect(Math.round(x+(ex-x)*i/(n||1)),Math.round(y+(ey-y)*i/(n||1)),w,w,c);
}
export function buildMonster():MonsterSprites{
  const dark='#191d29',base='#2a293b',mid='#39364b',edge='#5b5069',rim='#827080';
  function build(dir:'down'|'up'|'left',n:number){
    const p=new Painter(64,96,1),phase=n/MON_FRAMES*Math.PI*2,bob=monsterBob(n)*2,side=dir==='left';
    for(const [x,offset]of [[23,0],[36,Math.PI]]){
      const stride=Math.round(Math.sin(phase+offset)*4),lift=Math.round(Math.max(0,Math.cos(phase+offset))*3);
      line(p,x,72,x+stride,89-lift,7,C.outline);line(p,x+1,73,x+stride+1,88-lift,4,mid);
      p.rect(x+stride-2,90-lift,10,3,C.outline).hline(x+stride-2,90-lift,5,edge);
    }
    // A tapered, ragged coat. Shoulder folds and sleeves have their own volume.
    for(let y=28;y<80;y++){
      const inset=y<35?Math.max(9,20-(y-28)*2):y<60?17+Math.floor((y-35)/6):20-Math.floor((y-60)/4);
      p.hline(inset,y+bob,64-inset*2-(side?5:0),dark);
      p.hline(inset+2,y+bob,60-inset*2-(side?5:0),base);
    }
    for(const [x,sgn]of [[13,-1],[47,1]]){
      const swing=Math.round(Math.sin(phase+(sgn<0?0:Math.PI))*2);
      line(p,x,34+bob,x+sgn*4,54+bob,7,dark);line(p,x+sgn*4,54+bob,x+swing,75,6,dark);
      line(p,x+1,35+bob,x+sgn*4+1,53+bob,4,mid);line(p,x+sgn*4+1,54+bob,x+swing+1,73,3,base);
      for(let k=0;k<3;k++)line(p,x+swing+k*2-1,73,x+swing+k*2-2,78+k%2,1,edge);
      p.px(x,40+bob,rim);
    }
    line(p,24,34+bob,22,65+bob,2,mid);line(p,25,34+bob,23,55+bob,1,edge);
    line(p,35,37+bob,41,73+bob,2,mid);line(p,38,54+bob,43,75+bob,1,edge);
    line(p,31,43+bob,28,76+bob,1,edge);
    for(const [x,y]of [[18,76],[26,79],[36,78],[43,75]])p.rect(x,y+bob,3,4,C.outline).px(x,y+bob,edge);
    // Long-necked antler silhouette and an angular mask-like face.
    p.rect(25,23+bob,13,11,dark).rect(27,24+bob,8,8,mid);
    for(let y=7;y<28;y++){
      const inset=y<11?23-(y-7):y<20?19:19+Math.floor((y-20)/2);
      p.hline(inset,y+bob,64-inset*2-(side?4:0),dark);
      p.hline(inset+2,y+bob,60-inset*2-(side?4:0),base);
    }
    line(p,23,9+bob,17,1+bob,3,mid);line(p,18,4+bob,12,3+bob,2,edge);
    line(p,41,9+bob,47,0+bob,3,mid);line(p,46,4+bob,53,2+bob,2,edge);
    p.hline(23,9+bob,9,edge).hline(22,10+bob,7,mid).vline(20,12+bob,8,mid);
    if(dir!=='up'){
      p.rect(24,15+bob,7,5,dark).rect(26,16+bob,3,2,C.monsterEye).px(26,16+bob,C.monsterEyeHi);
      if(!side)p.rect(37,15+bob,7,5,dark).rect(40,16+bob,3,2,C.monsterEye).px(40,16+bob,C.monsterEyeHi);
      p.vline(32,18+bob,4,mid).hline(28,24+bob,11,dark).px(30,24+bob,edge).px(36,24+bob,edge);
    }else line(p,28,11+bob,29,24+bob,1,mid);
    return tagRaster(p.done(),2);
  }
  const down=Array.from({length:MON_FRAMES},(_,n)=>build('down',n)),up=Array.from({length:MON_FRAMES},(_,n)=>build('up',n)),left=Array.from({length:MON_FRAMES},(_,n)=>build('left',n));
  return {frames:{down,up,left,right:left.map(flipH)},eyes:{down:[[13,8],[20,8]],up:[],left:[[13,8]],right:[[17.5,8]]}};
}
